import React from "react";
import Container from "../components/Container";
import { motion } from "framer-motion";
import faroLogo from "../assets/faro.png";

const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  navyDeep: "#06122A",
  amber: "#FFC857",
  amberDark: "#E6B04E",
  white: "#F5F7FA",
  grayLight: "#9CA3AF",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

/* ─── primitives compartidas con el resto del sitio ─── */
function AmberSidebar({ opacity = 0.15 }) {
  return <div className="absolute top-0 right-0 w-2 md:w-3 h-full pointer-events-none"
    style={{ background: C.amber, opacity }} />;
}
function AmberRule({ width = "120px", className = "" }) {
  return <div className={`h-0.5 rounded-full ${className}`} style={{ width, background: C.amber }} />;
}
function FaroWatermark({ className = "", position = "left", opacity = 0.04 }) {
  return (
    <div className={`absolute pointer-events-none select-none ${className}`}
      style={{ transform: position === "right" ? "scaleX(-1)" : "none" }}>
      <img src={faroLogo} alt="" className="w-full h-full object-contain"
        style={{ opacity, filter: "brightness(2)" }} draggable={false} />
    </div>
  );
}

const content = {
  en: {
    kicker: "Contact",
    title: "Request a",
    titleAccent: "demo.",
    body: "A demo on your own data, no commitment.",
    responseTime: "We usually respond within 24 hours.",
    email: "contacto@farolabs.dev",
    direct: "Direct contact",
    follow: "Follow FARO",
    form: {
      heading: "Send us a message",
      name: "Name",
      email: "Email",
      product: "Which product are you interested in?",
      productOptions: ["RUMBO — Replenishment", "Demand planning", "Price optimization", "Promotion planning", "Not sure yet / the whole platform"],
      message: "Anything else you'd like to add?",
      messageSub: "Optional",
      submit: "Send Message",
    },
  },
  es: {
    kicker: "Contacto",
    title: "Solicitar",
    titleAccent: "demo.",
    body: "Una demo sobre tus propios datos, sin compromiso.",
    responseTime: "Generalmente respondemos en menos de 24 horas.",
    email: "contacto@farolabs.dev",
    direct: "Contacto directo",
    follow: "Seguinos",
    form: {
      heading: "Envianos un mensaje",
      name: "Nombre",
      email: "Email",
      product: "¿Qué producto te interesa?",
      productOptions: ["RUMBO — Reposición", "Planificación de la demanda", "Optimización de precios", "Planificación de promociones", "Todavía no sé / toda la plataforma"],
      message: "¿Algo más que quieras agregar?",
      messageSub: "Opcional",
      submit: "Enviar mensaje",
    },
  },
};

export default function ContactPage({ language }) {
  const t = content[language] || content.en;

  return (
    <>
      {/* ═══ HERO ═══ */}
      <section className="relative pt-10 md:pt-12 pb-6 md:pb-8 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
        <AmberSidebar />
        <FaroWatermark className="-top-10 -right-10 w-[50%] h-[110%]" position="right" />
        <Container className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              <img src={faroLogo} alt="FARO" className="w-12 h-12"
                style={{ filter: "drop-shadow(0 0 14px rgba(255,200,87,0.35))" }} />
            </div>
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] px-3.5 py-1.5 rounded-full mb-3"
              style={{ color: C.amber, background: "rgba(255,200,87,0.10)", border: "1px solid rgba(255,200,87,0.35)", fontFamily: MONO }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.amber, boxShadow: `0 0 8px ${C.amber}` }} />
              {t.kicker}
            </span>
            <h1 className="text-[32px] md:text-[42px] lg:text-[48px] font-bold leading-[1.05]"
              style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.02em" }}>
              {t.title}{" "}
              <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>
                {t.titleAccent}
              </span>
            </h1>
            <AmberRule width="72px" className="mt-4 mx-auto" />
          </motion.div>
        </Container>
      </section>

      {/* ═══ CONTENIDO — Info izquierda + Form derecha ═══ */}
      <section className="relative pt-6 md:pt-8 pb-10 md:pb-14"
        style={{ background: `linear-gradient(180deg, ${C.navyMid} 0%, ${C.navyDeep} 100%)` }}>
        <AmberSidebar opacity={0.08} />
        <Container className="relative z-10">
          <div className="grid gap-6 md:gap-8 md:grid-cols-[1fr_1.2fr] items-stretch max-w-6xl mx-auto">

            {/* ─── Left card: info ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative rounded-2xl p-6 md:p-7 overflow-hidden flex flex-col"
              style={{
                background: C.navyLight,
                border: "1px solid rgba(255,200,87,0.18)",
                boxShadow: "0 8px 24px rgba(11,31,58,0.25)",
              }}
            >
              {/* esquina ámbar decorativa */}
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: `linear-gradient(180deg, ${C.amber}, transparent)` }} />

              <span className="block text-[10.5px] font-bold uppercase tracking-[0.28em] mb-4"
                style={{ color: C.amber, fontFamily: MONO }}>
                {t.direct}
              </span>

              <a href={`mailto:${t.email}`}
                className="block text-[18px] md:text-[20px] font-bold mb-2 hover:underline"
                style={{ color: C.white, fontFamily: SANS }}>
                {t.email}
              </a>

              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[12px]" style={{ color: C.grayLight }}>{t.responseTime}</span>
              </div>
            </motion.div>

            {/* ─── Right card: formulario ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative rounded-2xl p-6 md:p-7 overflow-hidden"
              style={{
                background: C.navyLight,
                border: "1px solid rgba(255,200,87,0.18)",
                boxShadow: "0 8px 24px rgba(11,31,58,0.25)",
              }}
            >
              {/* esquina ámbar decorativa */}
              <div className="absolute top-0 right-0 w-1 h-full" style={{ background: `linear-gradient(180deg, ${C.amber}, transparent)` }} />

              <span className="block text-[10.5px] font-bold uppercase tracking-[0.28em] mb-2"
                style={{ color: C.amber, fontFamily: MONO }}>
                {t.form.heading}
              </span>
              <div className="w-10 h-px mb-4" style={{ background: C.amber }} />

              <form action="https://formsubmit.co/contacto@farolabs.dev" method="POST">
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_subject" value="New Contact — FARO" />

                <div className="space-y-3.5">
                  <div>
                    <label className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: C.grayLight }}>
                      {t.form.name} <span style={{ color: C.amber }}>*</span>
                    </label>
                    <input
                      name="name"
                      required
                      className="mt-1.5 w-full rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-amber-400"
                      style={{ background: C.navyMid, border: "1px solid rgba(255,255,255,0.10)", color: C.white }}
                    />
                  </div>

                  <div>
                    <label className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: C.grayLight }}>
                      {t.form.email} <span style={{ color: C.amber }}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="mt-1.5 w-full rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-amber-400"
                      style={{ background: C.navyMid, border: "1px solid rgba(255,255,255,0.10)", color: C.white }}
                    />
                  </div>

                  <div>
                    <label className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: C.grayLight }}>
                      {t.form.product}
                    </label>
                    <select
                      name="product"
                      defaultValue=""
                      className="mt-1.5 w-full rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-amber-400"
                      style={{ background: C.navyMid, border: "1px solid rgba(255,255,255,0.10)", color: C.white }}
                    >
                      <option value="" disabled>—</option>
                      {t.form.productOptions.map((opt) => (
                        <option key={opt} value={opt} style={{ background: C.navyMid, color: C.white }}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: C.grayLight }}>
                      {t.form.message} <span className="lowercase tracking-normal font-normal" style={{ color: "rgba(156,163,175,0.65)" }}>· {t.form.messageSub}</span>
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      className="mt-1.5 w-full rounded-xl px-4 py-2.5 text-sm outline-none transition resize-none focus:border-amber-400"
                      style={{ background: C.navyMid, border: "1px solid rgba(255,255,255,0.10)", color: C.white }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="mt-5 w-full rounded-xl text-[14px] font-bold py-3 transition-all hover:-translate-y-0.5"
                  style={{ background: C.amber, color: C.navy, boxShadow: "0 4px 24px rgba(255,200,87,0.32)", fontFamily: SANS }}
                >
                  {t.form.submit} →
                </button>
              </form>
            </motion.div>
          </div>
        </Container>
      </section>
    </>
  );
}
